import {ObjectStatus} from '@fundamental-ngx/core';

export enum ResourceType {
    FOLDER = 'Folder',
    RESOURCE_GROUP = 'ResourceGroup',
    ORGANIZATION = 'Organization',
    SERVICE_INSTANCE = 'ServiceInstance',
    RTD = 'ResourceTypeDefinition',
    VALIDATING_WEBHOOK_CONFIGURATION = 'ValidatingWebhookConfiguration',
    SERVICE_METADATA = 'ServiceMetadata',
    MANAGED_APPLICATION_PROVIDER = 'ManagedApplicationProvider',
    MANAGED_SERVICE = 'ManagedService',
    PATH_BINDING = 'PathBinding',
    TECHNICAL_CLIENT = 'TechnicalClient',
    SERVICE_PUBLISH_CONFIGURATION = 'ServicePublishConfiguration',
    SECRET = 'Secret',
    ACCOUNT = 'Account',
    ASSIGNMENT = 'Assignment',
    AUTOMATION_SOLUTION = 'AutomationSolution',
    SPFI_APPLICATION = 'SPFIApplication',

    ROLE = 'Role',
    ROLE_BINDING = 'Binding',
    ROLE_BINDING_OLD = 'RoleBinding',
    ALIAS = 'Alias',
    CONFIG_MAP = 'ConfigMap',
    YAML = 'YAML',
    SERVICE_INSTANCE_GENERIC = 'Service Instance',
    UNKNOWN = 'Unknown'
}

export enum WizardApplicationType {
    UNIFIED = 'unified',
    SUITE = 'suite'
}

export enum UniqueLabels {
    PROJECT_KEY = 'project',
    COST_OBJECT_KEY = 'cost-object',
}

export enum ResourceState {
    READY = 'Ready',
    FALLBACK_READY = 'True',
    NOT_READY = 'False',
    IN_PROCESS = 'Processing',
    TERMINATING = 'Deleting',
    FAILED = 'Failed',
    FALLBACK_TERMINATING = 'Terminating',
    UNKNOWN = 'Unknown',
    TERMINATION_FAILED = 'Termination Failed'
}

export const ResourceServerState: { [key: string]: ResourceState} = {
    'Processing':  ResourceState.IN_PROCESS,
    'Error': ResourceState.FAILED,
    'Ready': ResourceState.READY,
    'Terminating': ResourceState.TERMINATING,
    'TerminationFailed': ResourceState.TERMINATION_FAILED
};

/**
 * All possible AutomationSolution status condition reasons of type Ready.
 * Reference: https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/cloud-automation/#condition-reasons
 */

export const BASIC_CONDITION_REASONS = [
    'Processing',
    'Ready',
    'Error',
    'Terminating',
    'TerminationFailed',
    'Cancelled'
];

export enum Theme {
    ATOM_DARK = 'atom-dark',
    ATOM_LIGHT = 'atom-light',
    ATOM_HCB = 'atom-hcb',
}

export enum PolicyType {
    Assignment = 'Assignment',
    Access = 'Access',
    ServicePublishConfiguration = 'ServicePublishConfiguration',
    Authorizations = 'Authorizations',
}

export enum PolicyAction {
    All = '*',
    Update = 'update',
    Patch = 'patch',
    Delete = 'delete',
    Move = 'move'
}

export enum AuthorizationsGroupBy {
    Role = 'Role',
    User = 'User',
}

export class Const {
    public static ROOT_PATH = '/';
    public static NOT_LOGGED_IN_HTTP_CODE = 403;
    public static ALL_PROJECTS_PARAM = '_all';
    public static ANY_RESOURCE = '*';
    public static FOLDERS = 'folders';
    public static RESOURCE_GROUPS = 'resource-groups';

    public static ROUTE_PARAMETERS = {
        PROJECT: 'projectName',
        ORGANIZATION: 'orgName',
        FOLDER: 'folderName',
        RESOURCE_GROUP: 'resourceGroupName',
    };

    public static CONDITIONS: any = {
        TYPES: {
            READY: 'Ready'
        },
    };

    public static OBJECT_STATUS: { [key: string]: ObjectStatus } = {
        [ResourceState.READY]: 'positive',
        [ResourceState.FALLBACK_READY]: 'positive',
        [ResourceState.NOT_READY]: 'negative',
        [ResourceState.TERMINATING]: 'critical',
        [ResourceState.FALLBACK_TERMINATING]: 'critical',
        [ResourceState.TERMINATION_FAILED]: 'negative',
        [ResourceState.FAILED]: 'negative',
        [ResourceState.IN_PROCESS]: 'critical',
        [ResourceState.UNKNOWN]: 'informative'
    };

    public static NO_VALUE_LABEL = '(no value)';
    public static LABEL_KEY_EXP = '^([a-z0-9\\.]*\\/)?([a-z0-9]([-a-z0-9]*[a-z0-9])?)?$';
    public static ANNOTATION_KEY_EXP = '^([a-z0-9\\.]*\\/)?([a-z0-9]([-a-z0-9]*[a-z0-9])?)?$';
    public static LABEL_KEY_LENGTH = '^.{0,63}$';

    public static LABEL_VALUE_EXP = '^([a-z0-9A-Z]([-a-z0-9A-Z_\\\\.]*[a-z0-9A-Z])?)?$';
    public static DISPLAY_NAME_EXP = "^[a-zA-Z0-9\\s\\:\\.\\,\\-\\_\\#\\~\\/\\(\\)\\!\\?\\@\\'\\\\&\\$\\%\\[\\]+\\;]*$";
    public static TECHNICAL_NAME_EXP = '^[a-z0-9]([-a-z0-9]*[a-z0-9])?(\\.[a-z0-9]([-a-z0-9]*[a-z0-9])?)*$';
    public static PATH_EXP = '^(((\\/)([a-z0-9]([-a-z0-9]{0,61}[a-z0-9])?(\\.[a-z0-9]([-a-z0-9]{0,61}[a-z0-9])?)*)){1,10}|\\*|\\/)$';
    public static DESCRIPTION_EXP = "^[a-zA-Z0-9\\s\\:\\.\\,\\-\\_\\#\\~\\/\\(\\)\\!\\?\\@\\'\\\\&\\$\\%\\[\\]+\\;]*$";

    public static URM_GROUP_NAME_EXP = '^([a-z0-9]([-a-z0-9]*[a-z0-9])?(\\.[a-z0-9]([-a-z0-9]*[a-z0-9])?)*|(\\*))$';
    public static URM_GROUP_NAME_MAX_LENGTH = 255;
    public static TPT_EXP = "^[a-zA-Z0-9\\s\\:\\.,\\-\\_\\#\\~\\/\\(\\)\\!\\?\\@\\'\\\"\\&\\$\\%\\[\\]+\\;]*$";

    public static ICON_URL_EXP = '^(sap-icon:\\/\\/.+|https?:\\/\\/.+)$';
    public static HTTP_URL_EXP = '^https?:\\/\\/.+$';

    // GVT (Group/Version/Type) field patterns for Product Blueprint Modeler Application Templates.
    // Version: lowercase, starts with [a-z], ends with [a-z0-9], dashes allowed in between (e.g. v1, v1-beta).
    public static VERSION_EXP = '^[a-z][a-z0-9\\-]*[a-z0-9]$';
    // Type: PascalCase — first letter uppercase, then any letters/digits.
    public static TYPE_EXP = '^[A-Z][A-Za-z0-9]*$';

    public static STATUS_DISPLAY_NAMES: { [key: string]: string } = {
        [ResourceState.READY]: 'Ready',
        [ResourceState.FALLBACK_READY]: 'True',
        [ResourceState.NOT_READY]: 'False',
        [ResourceState.IN_PROCESS]: 'In Process',
        [ResourceState.TERMINATING]: 'Deleting',
        [ResourceState.FALLBACK_TERMINATING]: 'Terminating',
        [ResourceState.UNKNOWN]: 'Unknown',
        [ResourceState.FAILED]: 'Failed',
        [ResourceState.TERMINATION_FAILED]: 'Termination Failed',
    };

    public static STATUS_DISPLAY_NAMES_IN_REFERENCES: { [key: string]: string } = {
        [ResourceState.READY]: 'Ready',
        [ResourceState.FALLBACK_READY]: 'Ready',
        [ResourceState.NOT_READY]: 'Not Ready',
        [ResourceState.IN_PROCESS]: 'In Process',
        [ResourceState.TERMINATING]: 'Terminating',
        [ResourceState.FALLBACK_TERMINATING]: 'Terminating',
        [ResourceState.FAILED]: 'Failed',
        [ResourceState.TERMINATION_FAILED]: 'Termination Failed',
        [ResourceState.UNKNOWN]: 'Unknown',
    };

    public static RESOURCE_GLYPHS: { [key: string]: string } = {
        [ResourceType.FOLDER]: 'folder-blank',
        [ResourceType.RESOURCE_GROUP]: 'sap-box',
        [ResourceType.ORGANIZATION]: 'tree',
        [ResourceType.SERVICE_INSTANCE]: 'product',
        [ResourceType.TECHNICAL_CLIENT]: 'user-settings',
        [ResourceType.MANAGED_SERVICE]: 'developer-settings',
        [ResourceType.SECRET]: 'key',
        [ResourceType.ACCOUNT]: 'puzzle',
        [ResourceType.ASSIGNMENT]: 'cause',
        [ResourceType.ROLE]: 'permission',
        [ResourceType.ROLE_BINDING]: 'private',
        [ResourceType.ROLE_BINDING_OLD]: 'private',
        [ResourceType.SERVICE_PUBLISH_CONFIGURATION]: 'share-2',
        [ResourceType.CONFIG_MAP]: 'filter-fields',
        [ResourceType.ALIAS]: 'duplicate',
        [ResourceType.YAML]: 'attachment-html',
        [ResourceType.SERVICE_INSTANCE_GENERIC]: 'puzzle',
        [ResourceType.UNKNOWN]: 'warning'
    };

    //We use 'any' here because of FontAwesome type issues with custom icons
    public static FONT_AWESOME_RESOURCE_GLYPHS: { [key: string]: any } = {
        [ResourceType.FOLDER]: ['workspace', 'folder'],
        [ResourceType.RESOURCE_GROUP]: ['fas', 'cubes'],
        [ResourceType.ORGANIZATION]: ['fas', 'sitemap'],
        [ResourceType.SERVICE_INSTANCE]: ['workspace', 'serviceInstance'],
        [ResourceType.TECHNICAL_CLIENT]: ['workspace', 'technicalClient'],
        [ResourceType.MANAGED_SERVICE]: ['workspace', 'managedService'],
        [ResourceType.SECRET]: ['workspace', 'secret'],
        [ResourceType.ACCOUNT]: ['fas', 'cube'],
        [ResourceType.ASSIGNMENT]: ['workspace', 'assignment'],
        [ResourceType.ROLE]: ['fas', 'file-circle-check'],
        [ResourceType.ROLE_BINDING]: ['workspace', 'roleBinding'],
        [ResourceType.ROLE_BINDING_OLD]: ['workspace', 'roleBinding'],
        [ResourceType.SERVICE_PUBLISH_CONFIGURATION]: ['fas', 'share-nodes'],
        [ResourceType.CONFIG_MAP]: ['workspace', 'configMap'],
        [ResourceType.ALIAS]: ['workspace', 'alias'],
        [ResourceType.YAML]: ['workspace', 'yaml'],
        [ResourceType.RTD]: ['workspace', 'rtd'],
        [ResourceType.VALIDATING_WEBHOOK_CONFIGURATION]: ['workspace', 'webhook'],
        [ResourceType.SERVICE_METADATA]: ['workspace', 'serviceMetadata'],
        [ResourceType.SERVICE_INSTANCE_GENERIC]: ['fas', 'cube'],
        [ResourceType.UNKNOWN]: ['fas', 'cube']
    };

    public static FONT_AWESOME_STATE_GLYPHS: { [key: string]: any } = {
        [ResourceState.READY]: 'check',
        [ResourceState.FALLBACK_READY]: 'check',
        [ResourceState.NOT_READY]: 'xmark',
        [ResourceState.TERMINATION_FAILED]: 'xmark',
        [ResourceState.FAILED]: 'xmark',
        [ResourceState.TERMINATING]: 'rotate',
        [ResourceState.FALLBACK_TERMINATING]: 'rotate',
        [ResourceState.IN_PROCESS]: 'rotate',
        [ResourceState.UNKNOWN]: 'minus'
    };
    public static SYSTEM_RESOURCES: string[] = [
        ResourceType.SECRET,
        ResourceType.ALIAS,
        ResourceType.ROLE,
        ResourceType.ROLE_BINDING,
        ResourceType.ROLE_BINDING_OLD,
        ResourceType.CONFIG_MAP,
        ResourceType.SERVICE_PUBLISH_CONFIGURATION,
        ResourceType.ASSIGNMENT,
        ResourceType.SERVICE_INSTANCE,
        ResourceType.TECHNICAL_CLIENT,
        ResourceType.MANAGED_SERVICE,
        ResourceType.PATH_BINDING,
        ResourceType.RTD,
        ResourceType.VALIDATING_WEBHOOK_CONFIGURATION,
        ResourceType.SERVICE_METADATA
    ];

    public static RESOURCE_REF_URL: { [key: string]: string } = {
        [ResourceType.FOLDER]: 'folder-blank',
        [ResourceType.RESOURCE_GROUP]: 'product',
        [ResourceType.ORGANIZATION]: 'tree',
        [ResourceType.PATH_BINDING]:
            'https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/binding-policies/#pathbinding',
        [ResourceType.SERVICE_INSTANCE]:
            'https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/service-consumption/#ServiceInstance',
        [ResourceType.TECHNICAL_CLIENT]:
            'https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/system-resources/#technicalclient',
        [ResourceType.MANAGED_SERVICE]:
            'https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/managed-services/#managedservice',
        [ResourceType.SECRET]:
            'https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/system-resources/#secret',
        [ResourceType.ACCOUNT]: 'puzzle',
        [ResourceType.ASSIGNMENT]:
            'https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/entitlements-assignments/#assignment',
        [ResourceType.ROLE]:
            'https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/binding-policies/#role',
        [ResourceType.ROLE_BINDING]:
            'https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/binding-policies/#binding',
        [ResourceType.ROLE_BINDING_OLD]:
            'https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/binding-policies/#binding',
        [ResourceType.SERVICE_PUBLISH_CONFIGURATION]:
            'https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/commercial-integration/#servicepublishconfiguration',
        [ResourceType.ALIAS]:
            'https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/system-resources/#alias',
        [ResourceType.CONFIG_MAP]:
            'https://pages.github.tools.sap/unified-resource-manager/docs/concepts/capabilities/system_resources/config_map/',
        [ResourceType.SERVICE_INSTANCE_GENERIC]:
            'https://pages.github.tools.sap/atom-cfs/atom-docs/docs/atom-tools/workspace/#instantiating-resource-types',
        [ResourceType.RTD]:
            'https://pages.github.tools.sap/unified-resource-manager/docs/concepts/capabilities/system_resources/rtd/',
        [ResourceType.VALIDATING_WEBHOOK_CONFIGURATION]:
            'https://pages.github.tools.sap/unified-resource-manager/docs/concepts/capabilities/system_resources/admission_webhook_config/',
        [ResourceType.SERVICE_METADATA]:
            'https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/commercial-integration/#servicemetadata',
    };

    public static IMMEDIATELY_READY_RESOURCE_TYPES = [ResourceType.SECRET, ResourceType.ALIAS, ResourceType.CONFIG_MAP];


    public static POLICY_TYPE_DISPLAY_NAMES: { [key: string]: string } = {
        [PolicyType.Assignment]: 'Assignments',
        [PolicyType.Access]: 'Authorizations',
        [PolicyType.Authorizations]: 'Authorizations',
        [PolicyType.ServicePublishConfiguration]: 'Service Publish Configurations',
    };

    public static RESOURCE_TYPE_DISPLAY_NAMES: { [key: string]: string } = {
        [ResourceType.RESOURCE_GROUP]: 'resource group',
        [ResourceType.FOLDER]: 'folder',
        [ResourceType.ORGANIZATION]: 'organization',
        [ResourceType.MANAGED_SERVICE]: 'managed service',
        [ResourceType.SERVICE_PUBLISH_CONFIGURATION]: 'service publish configuration',
        [ResourceType.SERVICE_INSTANCE]: 'service instance',
    };

    public static RG_SYSTEM_RESOURCES: string[] = [
        ResourceType.ASSIGNMENT,
        ResourceType.ROLE,
        ResourceType.ROLE_BINDING,
        ResourceType.ROLE_BINDING_OLD,
        ResourceType.SECRET,
        ResourceType.SERVICE_INSTANCE,
        ResourceType.SERVICE_PUBLISH_CONFIGURATION,
        ResourceType.TECHNICAL_CLIENT,
    ];

    public static ORG_FOLDER_SYSTEM_RESOURCES: string[] = [
        ResourceType.ASSIGNMENT,
        ResourceType.ROLE,
        ResourceType.ROLE_BINDING,
        ResourceType.ROLE_BINDING_OLD,
        ResourceType.SERVICE_PUBLISH_CONFIGURATION,
    ];

    public static PROVISIONING_METHODS: { [key: string]: string } = {
        operator: 'Operator-based',
        callback: 'Lightweight (Callback)',
        external: 'External',
    };

    public static YAML_THEMES: { [key: string]: string } = {
        [Theme.ATOM_DARK]: 'ace/theme/pastel_on_dark',
        [Theme.ATOM_LIGHT]: 'ace/theme/crimson_editor',
        [Theme.ATOM_HCB]: 'ace/theme/gob',
    };

    public static HIDE_EXTERNAL_LINKS_HOST = ["ns2"];

    public static RESOURCE_TYPE_ORDER = [ResourceType.RESOURCE_GROUP, ResourceType.FOLDER];

    public static RM_DOCS_WORKSPACE =
        'https://pages.github.tools.sap/atom-cfs/atom-docs/docs/atom-tools/workspace/';
    public static CLI_DOCS_URL = 'https://pages.github.tools.sap/atom-cfs/atom-docs/docs/atom-tools/uctl/';
    public static RM_DOCS_URL = 'https://pages.github.tools.sap/atom-cfs/atom-docs/docs/';
    public static RM_ABOUT_ATOM_URL = 'https://sap.sharepoint.com/sites/201434/SitePages/IES-Enablement.aspx';
    public static SUPPORT_URL =
        'https://jira.tools.sap/secure/CreateIssueDetails!init.jspa?pid=91536&issuetype=1&priority=3&components=172922&summary=[Unified Services Frontend, Bug] <Enter short summary of the observed issue>&description=%7Bcolor%3A%23ff0000%7D%2AREAD%20ME%20FIRST%21%21%2A%7Bcolor%7D%0A%0A%7Bcolor%3A%23ff0000%7DBefore%20submitting%20your%20bug%20report%2C%20please%20read%20this%20description%20carefully.%7Bcolor%7D%0A%20%23%20%7Bcolor%3A%23ff0000%7DIn%20the%20%2ASummary%2A%20field%20above%3A%7Bcolor%7D%0A%7Bcolor%3A%23ff0000%7DReplace%20the%20text%20%3CEnter%20short%20summary%20of%20the%20observed%20issue%3E%20with%20something%20short%20but%20meaningful%7Bcolor%7D%0A%20%23%20%7Bcolor%3A%23ff0000%7DIn%20this%20%2ADescription%2A%20box%2C%20provide%20the%20following%20information%3A%7Bcolor%7D%0A%7Bcolor%3A%23ff0000%7D%2ASteps%20to%20reproduce%2A%20%28describe%20step%20by%20step%20exactly%20what%20led%20to%20the%20observed%20issue%29%7Bcolor%7D%0A%7Bcolor%3A%23ff0000%7D%2AImpact%2A%20%28describe%20the%20impact%20of%20the%20issue%20on%20the%20task%20that%20you%20were%20performing%29%7Bcolor%7D%0A%20%23%20%7Bcolor%3A%23ff0000%7DAttach%20%2Ascreenshots%2A%20%28optional%29.%7Bcolor%7D%0A%7Bcolor%3A%23ff0000%7DScreenshots%20for%20each%20of%20the%20described%20steps%20help%20us%20to%20get%20the%20issue%20solved%20faster%21%7Bcolor%7D%0A%20%23%20%7Bcolor%3A%23ff0000%7D%2ADelete%20this%20template%20text%2A%20%28i.e.%2C%20empty%20the%20entire%20Description%20field%29%20and%20replace%20it%20with%20the%20%22Steps%20to%20reproduce%22%20and%20%22Impact%22%20as%20requested%20in%20step%202.%7Bcolor%7D%0A%20%23%20%7Bcolor%3A%23ff0000%7DClick%20%2ACreate%2A%20%28scroll%20down%20to%20the%20bottom%20of%20this%20page%29.%7Bcolor%7D';
    public static TEAMS_URL = "https://teams.microsoft.com/l/channel/19%3afee683dcada8418c8aeb26ba40bfd5cb%40thread.tacv2/Forum?groupId=1d042ff7-0e12-4e9c-8a3a-2b535ab84539&tenantId=42f7676c-f455-423c-82f6-dc2d99791af7";
    public static DOWNLOAD_UCTL = "https://github.tools.sap/unified-services/uctl/releases/latest";
    public static NO_EVENT_USER_EMAILS = ['uriel.sapir@sap.com', 'sap.user@sap.com']; //Temporarily added user email until it is removed from tests. Not a security issue.

    public static FEATURE_RUNTIME_POLICIES = "workspace-runtime-policy";
    public static FEATURE_SOFT_SELECT = "soft-select";
    public static FEATURE_ENTITLEMENT_POLICIES = "workspace-entitlement-policies";

    public static FEATURE_RUNTIME_POLICIES_FOR_SERVER = "runtimePolicy";
    public static FEATURE_STAGING_PARAMETERS = "workspace-staging-parameters";
    public static FEATURE_CLOUD_AUTOMATION_DESIGNER_TYPE_SELECTOR = "workspace-cloud-automation-designer-type-selector";
    public static FEATURE_BTP_WIZARD = "workspace-btp-wizard";
    public static FEATURE_UNIFIED_WIZARD_EUPORIE = "workspace-unified-wizard-euporie";
    public static FEATURE_MOVE_RESOURCE = "workspace-move-resource";
    public static FEATURE_CAD_SKIP_AUTO_ASSIGN_PLANS= "workspace-cad-skip-auto-assign-plans";
    public static FEATURE_CAD_DUAL_LANDSCAPE = "workspace-cad-dual-landscape";
    public static FEATURE_ADVANCED_SEARCH = "workspace-advanced-search";
    public static FEATURE_AI_ASSISTANCE = "AIAssistance";
    public static RESOURCE_MOVING_IN_PROCESS = "Resource is being moved to a new location. Click for details.";
    public static PENDING_DELETION = "(pending deletion)";
    public static SOLUTION_OUTPUTS_BUILDING_BLOCK_NAME = "output";
    public static INVALID_CAD_FORMAT_OF_AUTOMATION_SOLUTION = "This automation solution cannot be displayed.\n Error Message: \n";
    public static STRING_FORMAT_CORRECT_AND_INTERPOLATED = "If you manually edited the solution, ensure that all properties are properly formatted and correctly interpolated.";

    public static MAX_ADVANCED_FILTER_RESULTS = 1000;
    public static DEFAULT_SUPPORT_COMPONENT = "BC-CP-MP-SYS";
}
